import { useEffect, useState } from "react";
import { MailCheck, MailWarning, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";

const STATUS_BADGE = {
  SENT: "bg-emerald-500/15 text-emerald-400",
  PENDING: "bg-amber-500/15 text-amber-400",
  PROCESSING: "bg-blue-500/15 text-blue-400",
  FAILED: "bg-rose-500/15 text-rose-400",
};

export default function EmailsPage() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/emails/status").then(({ data }) => setStatus(data)).catch((e) => toast.error(errMsg(e)));
    const params = filter ? `?status=${filter}` : "";
    api.get(`/admin/emails${params}`).then(({ data }) => setLogs(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [filter]);

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/emails/test");
      if (data.ok) toast.success(data.message);
      else toast.error(data.message || "Test e-postası gönderilemedi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-emails">
      <h1 className="font-display font-extrabold text-2xl text-white">E-posta Bildirimleri</h1>

      {status && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
            <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Sağlayıcı</p>
            <p className="text-white font-bold" data-testid="email-provider">{status.provider === "resend_managed" ? "Resend (Yönetilen)" : status.provider}</p>
            <p className={`text-xs mt-1 font-semibold ${status.is_live ? "text-emerald-400" : "text-amber-400"}`} data-testid="email-provider-status">
              {status.status_label}
            </p>
          </div>
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
            <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Gönderen</p>
            <p className="text-white font-bold">{status.from_name}</p>
            <p className="text-xs text-neutral-500 mt-1">Reply-To: {status.reply_to}</p>
          </div>
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
            <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Kuyruk</p>
            <div className="flex gap-4 text-sm">
              <span className="text-amber-400" data-testid="email-queue-pending">{status.queue.pending} bekliyor</span>
              <span className="text-emerald-400">{status.queue.sent} gönderildi</span>
              <span className="text-rose-400">{status.queue.failed} hatalı</span>
            </div>
            <p className="text-xs text-neutral-500 mt-1">Admin bildirimleri: {status.admin_email}</p>
          </div>
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5 flex flex-col justify-between">
            <p className="text-[11px] uppercase tracking-wider text-neutral-500">Test</p>
            <button onClick={sendTest} disabled={busy}
              className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-blue-500 disabled:opacity-50"
              data-testid="email-test-btn">
              <Send size={14} /> {busy ? "Gönderiliyor..." : "Test E-postası Gönder"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {[["", "Tümü"], ["SENT", "Gönderilen"], ["PENDING", "Bekleyen"], ["FAILED", "Başarısız"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`h-9 px-4 rounded-xl text-xs font-semibold transition-colors ${filter === v ? "bg-blue-600 text-white" : "bg-[#181B20] text-neutral-400 border border-[#272B33] hover:text-white"}`}
              data-testid={`email-filter-${v || "all"}`}>{l}</button>
          ))}
        </div>
        <button onClick={load} className="h-9 px-3 rounded-xl bg-[#181B20] border border-[#272B33] text-neutral-400 hover:text-white" data-testid="email-refresh">
          <RefreshCw size={15} />
        </button>
      </div>

      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]" data-testid="email-logs-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-[#272B33]">
              <th className="px-5 py-3.5">Şablon</th><th className="px-4 py-3.5">Alıcı</th><th className="px-4 py-3.5">Konu</th>
              <th className="px-4 py-3.5">Durum</th><th className="px-4 py-3.5">Deneme</th><th className="px-4 py-3.5">Tarih</th>
            </tr>
          </thead>
          <tbody>
            {logs?.items?.map((m) => (
              <tr key={m.email_id} className="border-b border-[#272B33]/50" data-testid={`email-row-${m.email_id}`}>
                <td className="px-5 py-3 font-mono-ed text-xs text-blue-400">{m.template}</td>
                <td className="px-4 py-3 text-white text-xs">{m.to}</td>
                <td className="px-4 py-3 text-neutral-400 text-xs max-w-[240px] truncate">{m.subject}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${STATUS_BADGE[m.status] || "bg-neutral-700 text-neutral-300"}`}>
                    {m.status === "SENT" ? <MailCheck size={11} /> : m.status === "FAILED" ? <MailWarning size={11} /> : null}
                    {m.status}
                    {m.status === "SENT" && m.is_live === false && <span className="text-amber-400 ml-1">MOCK</span>}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-neutral-500">{m.attempts}{m.last_error ? ` · ${m.last_error}` : ""}</td>
                <td className="px-4 py-3 text-xs text-neutral-500">{fmtDate(m.sent_at || m.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs?.items?.length === 0 && <p className="text-center text-neutral-500 text-sm py-12">Kayıt yok</p>}
      </div>
    </div>
  );
}
